"use client";

import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Calendar } from "lucide-react";
import config from "../../../config/config";

export default function EventsSection() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${config.NEXT_PUBLIC_API_URL}/api/public/p/event?limit=10`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setUpcomingEvents(data.data.posts || []);
          }
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const formatDate = (dateString) => {
    console.log("formatDate input:", dateString);
    if (!dateString) {
      console.log("No date string provided");
      return "Date TBA";
    }

    const date = new Date(dateString);
    console.log("Parsed date:", date);
    if (isNaN(date.getTime())) {
      console.log("Invalid date detected");
      return "Date TBA";
    }

    const result = date.toLocaleDateString("en-US", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    console.log("Formatted result:", result);
    return result;
  };

  const formatTime = (dateString) => {
    if (!dateString) return "Time TBA";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Time TBA";
    }

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getEventStatus = (startDate, endDate) => {
    if (!startDate) return { text: "UPCOMING", color: "bg-logoBlue" };

    const eventStartDate = new Date(startDate);
    const eventEndDate = endDate ? new Date(endDate) : eventStartDate;
    const currentDate = new Date();

    // Remove time component for date comparison
    const startDateOnly = new Date(
      eventStartDate.getFullYear(),
      eventStartDate.getMonth(),
      eventStartDate.getDate()
    );
    const endDateOnly = new Date(
      eventEndDate.getFullYear(),
      eventEndDate.getMonth(),
      eventEndDate.getDate()
    );
    const currentDateOnly = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate()
    );

    if (currentDateOnly < startDateOnly) {
      return { text: "UPCOMING", color: "bg-logoBlue" };
    } else if (
      currentDateOnly >= startDateOnly &&
      currentDateOnly <= endDateOnly
    ) {
      return { text: "ONGOING", color: "bg-logoGreen" };
    } else {
      return { text: "EXPIRED", color: "bg-logoRed" };
    }
  };

  const formatEventDate = (startDate, endDate) => {
    if (!startDate) return "Date TBA";

    const start = new Date(startDate);
    if (isNaN(start.getTime())) return "Date TBA";

    const startFormatted = start.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    if (!endDate || startDate === endDate) {
      return startFormatted;
    }

    const end = new Date(endDate);
    if (isNaN(end.getTime())) return startFormatted;

    const endFormatted = end.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    // Always show full date range for clarity
    return `${startFormatted} - ${endFormatted}`;
  };

  if (loading) {
    return (
      <div className="col-span-1 lg:col-span-1">
        <div className="bg-white border rounded-t-sm border-gray-200 overflow-hidden flex flex-col h-auto lg:h-[600px]">
          <div className="bg-primary text-primary-foreground h-10 sm:h-12 flex items-center px-3 sm:px-4 flex-shrink-0">
            <h2 className="text-base sm:text-lg font-bold">EVENTS</h2>
          </div>
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 flex-1 lg:overflow-hidden">
            <div className="animate-pulse space-y-3 sm:space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2 sm:space-y-3">
                  <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-2 sm:h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-2 sm:h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-1 lg:col-span-1">
      <div className="bg-white border rounded-t-sm border-gray-200 overflow-hidden flex flex-col h-auto lg:h-[600px]">
        {/* Header */}
        <div className="bg-primary text-primary-foreground h-10 sm:h-12 flex items-center px-3 sm:px-4 flex-shrink-0">
          <h2 className="text-base sm:text-lg font-bold">EVENTS</h2>
        </div>

        <div className="flex-1 lg:overflow-hidden bg-gray-50">
          {upcomingEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📅</div>
              <p>No events found</p>
            </div>
          ) : (
            <div className="p-2 sm:p-3 space-y-3 sm:space-y-4">
              {upcomingEvents.map((event, index) => (
                <div
                  key={event._id || event.id}
                  className="group cursor-pointer bg-white border border-gray-200 rounded-sm overflow-hidden transition-colors duration-200"
                  onClick={() => window.open(`/event/${event.slug}`, "_blank")}
                >
                  {/* Feature Image */}
                  {event.featuredImage && (
                    <div className="w-full h-32 sm:h-36 md:h-40 bg-gray-200 overflow-hidden relative">
                      <img
                        src={event.featuredImage}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      {/* Dynamic Event badge */}
                      <div
                        className={`absolute top-2 sm:top-3 left-2 sm:left-3 ${
                          getEventStatus(event.startDate, event.endDate).color
                        } text-white px-2 py-1 rounded-full text-xs font-semibold`}
                      >
                        {getEventStatus(event.startDate, event.endDate).text}
                      </div>
                    </div>
                  )}

                  <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                    <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 group-hover:text-logoBlue transition-colors line-clamp-2">
                      {event.title}
                    </h3>

                    {/* Description/Excerpt */}
                    {event.excerpt && (
                      <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 sm:line-clamp-3">
                        {event.excerpt}
                      </p>
                    )}

                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-logoBlue" />
                        <span className="text-xs sm:text-sm">
                          {formatEventDate(event.startDate, event.endDate)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <span className="text-logoRed hover:text-logoRed/80 text-xs sm:text-sm font-semibold">
                        View Event Details
                      </span>
                      <svg
                        className="w-3 h-3 sm:w-4 sm:h-4 text-logoRed group-hover:translate-x-1 transition-transform duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
